import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from 'primereact/button'
import { Message } from 'primereact/message'
import { PermissionSuffix } from '../../_generated/api/user-management'
import { ServiceStatus } from '../../_generated/api/structure'
import ClassNameBuilder from '../../tools/ClassNameBuilder'
import t from '../../tools/t'
import { useServiceWindowContext } from '../contexts/ServiceWindowContext'
import { useUserContext } from '../contexts/UserContext'
import ServiceWindowCompleteDialog from '../dialogs/ServiceWindowCompleteDialog'
import DateDisplay from './pure/DateDisplay'
import { Column, Row } from './pure/FlexContainer'
import PrimeIcon from './pure/PrimeIcon'
import ServiceWindowStatus from './ServiceWindowStatus'

export default function BoltConnectionServiceWindow({ archived }: { archived: boolean }) {
    const { next, nextLoading, nextError, refresh } = useServiceWindowContext()
    const { can } = useUserContext()
    const [completing, setCompleting] = useState(false)
    const [dismissed, setDismissed] = useState<string>()
    if (nextLoading) return <Message severity="info" text={t('service_window_loading')} />
    if (nextError)
        return (
            <Row gap={1}>
                <Message severity="error" text={t('service_window_load_failed')} />
                <Button label={t('service_window_retry')} onClick={refresh} />
            </Row>
        )
    if (!next) return null
    const occurrenceKey = `${next.id}:${next.data.nextService?.occurrenceId}`
    if (dismissed === occurrenceKey)
        return (
            <Link to={next.referenceLink}>
                {t('service_window_view')}: {next.name}
            </Link>
        )
    return (
        <>
            <section
                className={new ClassNameBuilder()
                    .add('service-window-banner')
                    .addIf(
                        'service-window-banner--overdue',
                        next.data.status === ServiceStatus.Overdue
                    )
                    .toString()}
                aria-label={t('service_window_title')}
            >
                <div className="service-window-banner__icon">
                    <PrimeIcon name="wrench" />
                </div>
                <Column grow gap={0.5}>
                    <strong>
                        {next.data.status === ServiceStatus.Overdue
                            ? t('service_window_due')
                            : t('service_window_upcoming')}
                    </strong>
                    <Row gap={0.75} alignment="center" className="service-window-wrap">
                        <Link to={next.referenceLink}>{next.name}</Link>
                        <DateDisplay
                            value={next.data.nextService?.scheduledFor}
                            target={`next-${next.id}`}
                        />
                        <ServiceWindowStatus status={next.data.status} />
                    </Row>
                </Column>
                <Link to={next.referenceLink}>{t('service_window_view')}</Link>
                {can(PermissionSuffix.StructureWrite) && next.canComplete && !archived && (
                    <Button
                        label={t('service_window_serviced')}
                        onClick={() => setCompleting(true)}
                    />
                )}
                <Button
                    text
                    rounded
                    icon="pi pi-times"
                    aria-label={t('service_window_dismiss')}
                    onClick={() => setDismissed(occurrenceKey)}
                />
            </section>
            {completing && (
                <ServiceWindowCompleteDialog window={next} onHide={() => setCompleting(false)} />
            )}
        </>
    )
}
